import * as React from "react";
import Link from 'gatsby-link';
import { GoogleAnalyticsEventType } from "../../models/google-analytics";
import * as googleAnalyticsService from "../../services/google-analytics-service";
import styled from "styled-components";

/** PricingTableFeature */
interface PricingTableFeature {
  /** NAZWA FUNKCJI */
  featureName: string

  /** INFORMACJA CZY JEST TOOLTIP */
  featureHasTooltip: boolean

  /** OPIS FUNKCJI => W TOOLTIPIE */
  featureDescription: string
}

/** PricingTablePackageFeatures */
interface PricingTablePackageFeaturesProps {
  /** TYTUŁ TABELI */
  packageFeaturesTitle: string

  /** PODTYTUŁ TABELI */
  packageFeaturesSubtitle: string

  /** WIELKOŚĆ ELEMENTU BOOTSTRAP */
  packageFeatureColumnSize: number

  /** FICZERY */
  packageFeatures: PricingTableFeature[]
}

const PricingTablePackageFeatures = (props: PricingTablePackageFeaturesProps) => {
  const {packageFeaturesTitle,packageFeaturesSubtitle,packageFeatureColumnSize,packageFeatures} = props;
  return (
    <PricingTablePackageFeaturesWrapper className={`col-md-${packageFeatureColumnSize} col-sm-${packageFeatureColumnSize} pricing-col list-feature`}>
      <div className="pricing-card">
        <div className="pricing-header">
          <h5>{packageFeaturesTitle}</h5>
          <p style={{fontStyle:"normal"}}>{packageFeaturesSubtitle}</p>
        </div>
        <div className="pricing-feature">
          {packageFeatures.map((feature: PricingTableFeature, index: number) => {
            if (feature.featureHasTooltip) {
              return (
                <li key={index}>
                  <p>
                    <a className="tooltip">
                      <span className="tooltip-text">
                        {feature.featureName}
                        <span className="tooltip-content">{feature.featureDescription}</span>
                      </span>
                    </a>
                  </p>
                </li>
              )
            } else {
              return (
                <li key={index}>
                  <p>{feature.featureName}</p>
                </li>
              )
            }
           })}
        </div>
      </div>
    </PricingTablePackageFeaturesWrapper>
  )
};

/** STYLES => PricingTablePackageFeatures */

const PricingTablePackageFeaturesWrapper = styled.div`
  max-width: 270px;
  p {
    text-transform: initial !important;
  }
  span.tooltip-text {
    border-bottom: 1px dotted;
    text-transform: initial !important;
  }
  span {
    float: right;
    text-align: left;
    margin-left: auto;
    margin-right: auto;
    left: 0;
    right: 0;
  }
  span:before {
    left: 50% !important;
    margin-left: -10px;
  }
  span.tooltip-content {
    text-transform: initial !important;
  }
  a {
    color: #fff !important;
    display: block !important;
    border-bottom: none !important;
    opacity: 1 !important;
  }
  a:visited {
    color: #fff !important;
  }
`;

/** PricingTablePackageFeature */
interface PricingTablePackageFeature {
  /** INFORMACJA CZY TEXT CZY IKONKA */
  featureType: "text" | "icon"

  /** INFO TEXT => TYLKO DLA featureType: "text" */
  featureText?: string

  /** INFO CZY FEATURE INCLUDED => featureType: "icon" */
  featureIncluded?: boolean
}

/** PricingTablePackage */
interface PricingTablePackageProps {
  /** WIELKOŚĆ ELEMENTU BOOTSTRAP */
  packageFeatureColumnSize: number

  /** NAZWA PAKIETU */
  packageName: string

  /** CENA PAKIETU W PLN */
  packagePrice: number

  /** INFORMACJA O TYM CZY UMIESZCZAĆ SŁOWO "NETTO" NAD INFORMACĄ DO CENY */
  packagePricingNetInfoVisible?: boolean

  /** INFORMACJA DO CENY => NP: "ZŁ / MC" */
  packagePricingInfo: string

  /** FICZERY */
  packageFeatures: PricingTablePackageFeature[]

  /** CZY MA BYĆ GWIAZDKA PRZY PAKIECIE */
  packageIsFeatured: boolean

  /** CZY MA BYĆ OBECNIE WYBRANYM */
  packageIsCurrent: boolean

  /** INFORMACJA CZY MA BYĆ GUZIK */
  packageButtonHidden: boolean

  /** TEKST GUZIKA POD TABELKĄ */
  packageButtonText?: string

  /** ADRES GDZIE MA NAS PRZENOSIĆ GUZIK */
  packageButtonLinkTo?: string

  /** INFORMACJA CZY LINK MA SCROLOWAĆ */
  packageButtonLinkIsForScrolling?: boolean

  /** FUNKCJA PRZY WYBRANIU PAKIETU */
  onPackageSelect?: () => any
}

const PricingTablePackage = (props: PricingTablePackageProps) => {
  const {
    packageFeatureColumnSize,
    packageName,
    packagePrice,
    packagePricingInfo,
    packageFeatures,
    packageIsFeatured,
    packageIsCurrent,
    packageButtonHidden,
    packageButtonText,
    packageButtonLinkTo,
    packageButtonLinkIsForScrolling,
    onPackageSelect,
  } = props;
  return (
    <div className={packageIsCurrent ? `col-md-${packageFeatureColumnSize} col-sm-${packageFeatureColumnSize} pricing-col current` : `col-md-${packageFeatureColumnSize} col-sm-${packageFeatureColumnSize} pricing-col`}>
      <div className="pricing-card">
        <div className="pricing-header">
          <PricingTablePackageNameWrapper>{packageName}</PricingTablePackageNameWrapper>
          {packageIsFeatured &&
            <PricingTableFeaturedRibbonWrapper className="ribbon">
              <i className="fa fa-star"></i>
              <span>Popularny</span>
            </PricingTableFeaturedRibbonWrapper>
          }
          <div className="price-box">
            <div className="price">
              {packagePrice}
              <div className="plan">{packagePricingInfo}</div>
              {props.packagePricingNetInfoVisible && <PricingTablePackagePriceInfoDetailsWrapper className="plan">NETTO</PricingTablePackagePriceInfoDetailsWrapper>}
            </div>
          </div>
        </div>
        <div className="pricing-feature">
          {packageFeatures.map((feature: PricingTablePackageFeature, index: number) => {
            if (feature.featureType == "icon") {
              return (
                <li key={index}>
                  <p>
                    <i className={feature.featureIncluded ? `fa fa-check available` : `fa fa-times available`}></i>
                  </p>
                </li>
              )
            } else {
              return (
                <li key={index}>
                  <p>
                    <FeatureTextWrapper>{feature.featureText}</FeatureTextWrapper>
                  </p>
                </li>
              )
            }
          })}
        </div>

        {!packageButtonHidden && !packageButtonLinkIsForScrolling &&
          <PricingTablePackageFooterWrapper className="pricing-footer">
            <Link to={packageButtonLinkTo} className="btn btn-act rounded btn-line" onClick={onPackageSelect}>
              <span>{packageButtonText}</span>
              <i className="fa fa-arrow-right"/>
            </Link>
          </PricingTablePackageFooterWrapper>
        }

        {!packageButtonHidden && packageButtonLinkIsForScrolling &&
          <PricingTablePackageFooterWrapper className="pricing-footer">
            <a href={packageButtonLinkTo} className="btn btn-act rounded btn-line" onClick={onPackageSelect}>
              <span>{packageButtonText}</span>
              <i className="fa fa-arrow-right"/>
            </a>
          </PricingTablePackageFooterWrapper>
        }
      </div>
    </div>
  )
};

/** STYLES => PricingTablePackage */

const PricingTablePackageNameWrapper = styled.h5`
  font-weight: 600;
`;

const PricingTableFeaturedRibbonWrapper = styled.a`
  &:hover {
    width: 110px !important;  
  }
`;

const PricingTablePackagePriceInfoDetailsWrapper = styled.div`
  padding-bottom: 14px;
`;

const FeatureTextWrapper = styled.span`
  text-transform: initial !important;
`;

const PricingTablePackageFooterWrapper = styled.div`
  i {
    margin-top: -3px;
  }
`;

/** PricingTableBlock */
interface PricingTableBlockProps {
  /** INFORMACJA O PAKIETACH */
  packagesInfo: PricingTablePackageFeaturesProps

  /** OFEROWANE PAKIETY */
  packages: PricingTablePackageProps[]

  /** GOOGLE EVENT TO TRIGGER ON SECTION VIEW */
  componentViewEventType?: GoogleAnalyticsEventType

  /** GOOGLE EVENT TYPE TO TRIGER ON BUTTON CLICK */
  buttonClickEventType?: GoogleAnalyticsEventType
}

interface PricingTableBlockState {}

class PricingTableBlock extends React.Component<PricingTableBlockProps,PricingTableBlockState> {

  constructor(props: PricingTableBlockProps) {
    super(props);
    this.onComponentView = this.onComponentView.bind(this);
    this.onButtonClick = this.onButtonClick.bind(this);
  }

  onComponentView() {
    if (this.props.componentViewEventType) {
      googleAnalyticsService.trackEvent(this.props.componentViewEventType);
    }
  }

  onButtonClick() {
    if (this.props.buttonClickEventType) {
      googleAnalyticsService.trackEvent(this.props.buttonClickEventType);
    }
  }

  render() {
    const {packagesInfo,packages} = this.props;
    return (
      <div className="matraz-theme-styles" onMouseEnter={this.onComponentView}>
        <PricingTableBlockWrapper className="pricing-wrapper comparison-table clearfix color-pallet-blue style-2">
          <PricingTablePackageFeatures {...packagesInfo}/>
          {packages.map((packageData: PricingTablePackageProps, index: number) =>
            <PricingTablePackage
              key={index}
              {...packageData}
              onPackageSelect={this.onButtonClick}
            />
          )}
        </PricingTableBlockWrapper>
      </div>
    )
  }

}

/** STYLES => PricingTableBlock */

const PricingTableBlockWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  width: 80%;
  @media (max-width: 1023px) {
    width: 95%;
  }
`;

export default PricingTableBlock


// WEBPACK FOOTER //
// ./src/layouts/pricing-table-block/pricing-table-block.tsx